import { Observable, ReplaySubject, catchError, filter, share, take, tap, throwError, timer } from 'rxjs';

export interface CachedStreamOptions {
  /** Cache time-to-live in milliseconds. Default: 10000ms (10 seconds) */
  ttl: number | (() => number);
  /** Function to extract cache key from method arguments */
  keyExtractor: (...args: unknown[]) => string;
  /** Optional shared cache key for multiple methods to use the same cache */
  sharedCacheKey?: string;
}

const STREAM_CACHE_KEY = Symbol('StreamCache');
const TTL_REGISTRY = new Map<string, number>();

type CachedInstance = {
  [STREAM_CACHE_KEY]?: Map<string, Map<string, Observable<unknown>>>;
};

/**
 * Method decorator for caching stream results with automatic cleanup
 *
 * @param options - Configuration options for caching behavior
 *
 * @example
 * ```typescript
 * // Basic usage - each method has its own cache
 * @CachedStream({
 *   ttl: 5 * 1000,
 *   keyExtractor: (id: string, context?: string) => context ? `${id}_${context}` : id
 * })
 * loadData(id: string, context?: string): Observable<Data> {
 *   return this.apiService.getData(id, context);
 * }
 *
 * // Shared cache key - multiple methods share the same cache and streams
 * // IMPORTANT: All methods with the same sharedCacheKey MUST have the same TTL
 * @CachedStream({
 *   ttl: 10 * 1000, // same TTL for all methods using 'userData'
 *   sharedCacheKey: 'userData',
 *   keyExtractor: (id: string) => id // same key = shared stream
 * })
 * loadUser(id: string): Observable<User> {
 *   return this.apiService.getUser(id);
 * }
 *
 * @CachedStream({
 *   ttl: 10 * 1000, // same TTL as above
 *   sharedCacheKey: 'userData', // same cache
 *   keyExtractor: (id: string) => id // same key = shared stream
 * })
 * getCurrentUser(id: string): Observable<User> {
 *   return this.apiService.getUser(id);
 * }
 *
 * // Usage:
 * this.loadUser('123');     // API call, stream cached
 * this.getCurrentUser('123'); // returns the SAME cached stream (no new API call)
 * ```
 */
export function CachedStream(options: CachedStreamOptions) {
  const { ttl, keyExtractor, sharedCacheKey } = options;

  if (sharedCacheKey) {
    if (TTL_REGISTRY.has(sharedCacheKey)) {
      const existingTtl = TTL_REGISTRY.get(sharedCacheKey);
      if (existingTtl !== getTtl(ttl)) {
        throw new Error(`All methods using the same sharedCacheKey must have identical TTL values.`);
      }
    } else {
      TTL_REGISTRY.set(sharedCacheKey, getTtl(ttl));
    }
  }

  return function <T extends unknown[], R>(
    target: unknown,
    propertyKey: string,
    descriptor: TypedPropertyDescriptor<(...args: T) => Observable<R>>,
  ): TypedPropertyDescriptor<(...args: T) => Observable<R>> {
    const originalMethod = descriptor.value;

    descriptor.value = function (this: CachedInstance, ...args: T): Observable<R> {
      if (!this[STREAM_CACHE_KEY]) {
        this[STREAM_CACHE_KEY] = new Map<string, Map<string, Observable<unknown>>>();
      }

      const instanceCache = this[STREAM_CACHE_KEY];

      const cacheMethodKey = sharedCacheKey || propertyKey;

      if (!instanceCache.has(cacheMethodKey)) {
        instanceCache.set(cacheMethodKey, new Map());
      }

      const methodCache = instanceCache.get(cacheMethodKey);

      const cacheKey = keyExtractor(...args);

      if (!methodCache.has(cacheKey)) {
        const stream$ = originalMethod.apply(this, args).pipe(
          filter((result) => !!result),
          take(1),
          catchError((error) => {
            methodCache.delete(cacheKey);

            return throwError(() => error);
          }),
          share({
            connector: () => new ReplaySubject(1),
            resetOnComplete: () =>
              timer(getTtl(ttl)).pipe(
                tap(() => {
                  methodCache.delete(cacheKey);
                }),
              ),
          }),
        );

        methodCache.set(cacheKey, stream$);
      }

      return methodCache.get(cacheKey) as Observable<R>;
    };

    return descriptor;
  };
}

/**
 * Method decorator that automatically clears related caches after method execution
 *
 * @param methodNames - Name(s) of cached methods to clear
 * @param keyExtractor - Optional function to extract specific keys to clear from method arguments
 *
 * @example
 * ```typescript
 * // Clear specific key from one method
 * @ClearCache('loadData', (id: string) => id)
 * updateData(id: string, data: UpdateData): Observable<Data> {
 *   return this.apiService.updateData(id, data);
 * }
 *
 * // Clear entire cache for multiple methods
 * @ClearCache(['loadData', 'loadList'])
 * deleteData(id: string): Observable<void> {
 *   return this.apiService.deleteData(id);
 * }
 *
 * // Clear multiple keys from multiple methods
 * @ClearCache(['userCache', 'profileCache'], (id: string) => [`user_${id}`, `profile_${id}`])
 * updateUser(id: string, user: User): Observable<User> {
 *   return this.apiService.updateUser(id, user);
 * }
 *
 * // Clear shared cache (when using sharedCacheKey)
 * @ClearCache('userCache', (id: string) => [`user_${id}`, `profile_${id}`])
 * deleteUser(id: string): Observable<void> {
 *   return this.apiService.deleteUser(id);
 * }
 * ```
 */
export function ClearCache(methodNames: string | string[], keyExtractor?: (...args: unknown[]) => string | string[]) {
  return function <T extends unknown[], R>(
    target: unknown,
    propertyKey: string,
    descriptor: TypedPropertyDescriptor<(...args: T) => R>,
  ): TypedPropertyDescriptor<(...args: T) => R> {
    const originalMethod = descriptor.value;

    descriptor.value = function (this: CachedInstance, ...args: T): R {
      const result = originalMethod.apply(this, args);

      const instanceCache = this[STREAM_CACHE_KEY];
      if (!instanceCache) return result;

      const methods = Array.isArray(methodNames) ? methodNames : [methodNames];

      if (keyExtractor) {
        const keys = keyExtractor(...args);
        const keysArray = Array.isArray(keys) ? keys : [keys];

        for (const method of methods) {
          const methodCache = instanceCache.get(method);
          if (methodCache) {
            for (const key of keysArray) {
              methodCache.delete(key);
            }
          }
        }
      } else {
        for (const method of methods) {
          const methodCache = instanceCache.get(method);
          if (methodCache) {
            methodCache.clear();
          }
        }
      }

      return result;
    };

    return descriptor;
  };
}

function getTtl(ttl: number | (() => number)) {
  return typeof ttl === 'function' ? ttl() : ttl;
}
